import {
  LayoutDashboard,
  Package,
  MessageCircle,
  Settings,
  User,
  ChevronLeft,
  ChevronRight,
  MapPin,
  Users,
  Sparkles,
  GraduationCap,
} from 'lucide-react';
import { Button } from './ui/button';
import { cn } from './ui/utils';
import { Link, useLocation } from 'react-router-dom';

const Logo = '/assets/logo/findecor-logo.svg';

interface MenuItem {
  id: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  path: string;
}

interface SidebarProps {
  collapsed: boolean;
  onToggleCollapse: () => void;
  onSignOut?: () => void;
  userInfo?: {
    name: string;
    email: string;
  };
  isMobile?: boolean;
  mobileMenuOpen?: boolean;
  onMobileMenuClose?: () => void;
}

const menuItems: MenuItem[] = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    icon: LayoutDashboard,
    path: '/dashboard',
  },
  {
    id: 'products-management',
    label: 'Product Management',
    icon: Package,
    path: '/products-management',
  },
  {
    id: 'users',
    label: 'User Management',
    icon: Users,
    path: '/user-management',
  },
  {
    id: 'branches',
    label: 'Branch Management',
    icon: MapPin,
    path: '/branch-management',
  },
  { id: 'showroom', label: 'AI Showroom', icon: Sparkles, path: '/showroom' },
  {
    id: 'ai-training',
    label: 'AI Training',
    icon: GraduationCap,
    path: '/ai-training',
  },
  { id: 'chat', label: 'Chat', icon: MessageCircle, path: '/chat' },
  {
    id: 'integration',
    label: 'Integration',
    icon: Settings,
    path: '/integration',
  },
  { id: 'profile', label: 'Profile', icon: User, path: '/profile' },
];

export function Sidebar({
  collapsed,
  onToggleCollapse,
  userInfo = { name: 'Admin User', email: 'admin@findecor.com' },
  isMobile = false,
  mobileMenuOpen = false,
  onMobileMenuClose,
}: SidebarProps) {
  const location = useLocation();

  const isActive = (path: string) => {
    return location.pathname.startsWith(path);
  };

  const sidebarClasses = cn(
    'bg-sidebar border-r border-sidebar-border transition-all duration-300 z-40',
    isMobile
      ? cn(
          'fixed inset-y-0 left-0 w-[200px] transform',
          mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
        )
      : cn('fixed left-0 top-0 h-full', collapsed ? 'w-19' : 'w-64')
  );

  return (
    <div className={sidebarClasses}>
      <div className="flex flex-col h-full">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-sidebar-border">
          {(!collapsed || isMobile) && (
            <Link to="/dashboard" className="flex items-center space-x-2 p-2">
              <img
                src={Logo}
                alt="Findecor logo"
                className="w-full object-contain"
              />
            </Link>
          )}
          {!isMobile && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onToggleCollapse}
              className="text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground cursor-pointer"
            >
              {collapsed ? (
                <ChevronRight className="h-4 w-4" />
              ) : (
                <ChevronLeft className="h-4 w-4" />
              )}
            </Button>
          )}
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-2">
          {menuItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.path);

            return (
              <Link
                key={item.id}
                to={item.path}
                className={cn(
                  'flex items-center w-full p-2 rounded-lg text-sm font-medium transition-colors',
                  collapsed && !isMobile ? 'px-3' : 'px-4',
                  active
                    ? 'bg-sidebar-primary text-sidebar-primary-foreground'
                    : 'text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground'
                )}
                onClick={() => isMobile && onMobileMenuClose?.()}
              >
                <Icon
                  className={cn('h-5 w-5 flex-shrink-0', !collapsed && 'mr-3')}
                />
                {(!collapsed || isMobile) && (
                  <span className="truncate">{item.label}</span>
                )}
              </Link>
            );
          })}
        </nav>

        {/* Footer */}
        {(!collapsed || isMobile) && (
          <div className="p-4 border-t border-sidebar-border">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-sidebar-accent rounded-full flex items-center justify-center">
                <User className="h-4 w-4 text-sidebar-accent-foreground" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-sidebar-foreground font-medium truncate">
                  {userInfo.name}
                </p>
                <p className="text-xs text-sidebar-foreground/60 truncate">
                  {userInfo.email}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
